import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import apiUser from '../../apiUser';
import { fmtBRL, fmtCompExtenso } from '../Faturamento/format';
import OrigemMudanca from './OrigemMudanca';

const STATUS_LABEL = {
    aguardando: 'Aguardando resposta', aprovado: 'Aprovado', contestado: 'Contestado',
    expirado: 'Expirado', faturado: 'Faturado', erro: 'Erro de envio',
};

const STATUS_BANNER = {
    aprovado: 'bg-blue-50 text-blue-900',
    contestado: 'bg-orange-50 text-orange-900',
    expirado: 'bg-gray-100 text-gray-800',
    faturado: 'bg-green-50 text-green-900',
    erro: 'bg-red-50 text-red-900',
};

const TIPO_MUDANCA = { novo: 'Novo', removido: 'Saiu', alterado: 'Mudou' };
const fmtDiferenca = (v) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${fmtBRL(Math.abs(v))}`;

const FaturamentoPublico = () => {
    const { hash } = useParams();
    const [searchParams] = useSearchParams();
    const [dados, setDados] = useState(null);
    const [erro, setErro] = useState(null);
    const [loading, setLoading] = useState(true);
    const [modo, setModo] = useState(searchParams.get('acao') === 'contestar' ? 'contestar' : null);
    const [form, setForm] = useState({ nome: '', email: '', mensagem: '' });
    const [feedback, setFeedback] = useState(null);
    const [enviando, setEnviando] = useState(false);

    useEffect(() => {
        apiUser.post('/fatPublic/dados', { hash })
            .then(({ data }) => {
                if (data.success) setDados(data.dados);
                else setErro(data.message);
            })
            .catch(() => setErro('Erro ao carregar. Tente novamente mais tarde.'))
            .finally(() => setLoading(false));
    }, [hash]);

    const aprovar = async () => {
        if (!form.nome.trim()) { setFeedback('Informe seu nome para confirmar.'); return; }
        setEnviando(true);
        try {
            const { data } = await apiUser.post('/fatPublic/aprovar', { hash, nome: form.nome });
            if (data.success) setDados({ ...dados, status: 'aprovado', respondivel: false });
            setFeedback(data.message);
        } catch { setFeedback('Erro ao enviar. Tente novamente.'); }
        setEnviando(false);
    };

    const contestar = async () => {
        if (!form.mensagem.trim()) { setFeedback('Descreva o motivo da contestação.'); return; }
        setEnviando(true);
        try {
            const { data } = await apiUser.post('/fatPublic/contestar', { hash, ...form });
            if (data.success) setDados({ ...dados, status: 'contestado', respondivel: false });
            setFeedback(data.message);
        } catch { setFeedback('Erro ao enviar. Tente novamente.'); }
        setEnviando(false);
    };

    if (loading) return <div className='min-h-screen flex items-center justify-center text-gray-600'>Carregando...</div>;
    if (erro) return (
        <div className='min-h-screen flex items-center justify-center bg-gray-50 p-4'>
            <div className='bg-white rounded-xl shadow p-8 text-center max-w-md'>
                <h1 className='text-xl font-bold text-gray-800 mb-2'>Link inválido ou expirado</h1>
                <p className='text-gray-600'>{erro}</p>
            </div>
        </div>
    );
    if (!dados) return null;

    const snap = dados.snapshot || { itens: [], totais: {} };
    return (
        <div className='min-h-screen bg-gray-50 py-8 px-4'>
            <div className='max-w-3xl mx-auto'>
                <div className='text-white rounded-t-xl px-6 py-5' style={{ backgroundColor: '#142a4c' }}>
                    <h1 className='text-2xl font-bold'>Relatório de Faturamento</h1>
                    <p className='mt-1 opacity-90'>{dados.cliente_nome} — Competência {fmtCompExtenso(dados.competencia)}</p>
                    {dados.contrato_nome && (
                        <p className='mt-1 opacity-90'>{dados.contrato_nome}{dados.contrato_numero ? ` (nº ${dados.contrato_numero})` : ''}</p>
                    )}
                </div>
                <div className='bg-white rounded-b-xl shadow px-4 sm:px-6 py-6'>
                    {dados.status === 'expirado' && dados.respondivel && (
                        <div className='mb-4 p-3 rounded-lg bg-amber-50 text-amber-900'>
                            O prazo sugerido para resposta passou, mas você ainda pode aprovar ou contestar este faturamento.
                        </div>
                    )}
                    {dados.status !== 'aguardando' && !(dados.status === 'expirado' && dados.respondivel) && (
                        <div className={`mb-4 p-3 rounded-lg font-semibold ${STATUS_BANNER[dados.status] || 'bg-gray-100 text-gray-800'}`}>
                            Status: {STATUS_LABEL[dados.status] || dados.status}
                        </div>
                    )}
                    {dados.mudancas?.length > 0 && (
                        <div className='mb-4 p-4 rounded-lg border border-amber-200 bg-amber-50'>
                            <h2 className='font-bold text-amber-900 mb-2'>
                                O que mudou em relação a {dados.competencia_anterior ? fmtCompExtenso(dados.competencia_anterior) : 'o mês anterior'}
                            </h2>
                            <div className='overflow-x-auto'>
                                <table className='w-full text-sm min-w-[480px]'>
                                    <thead>
                                        <tr className='text-left text-amber-900'>
                                            <th className='py-1.5 pr-3 font-semibold'>Item</th>
                                            <th className='py-1.5 px-3 text-center font-semibold whitespace-nowrap'>Qtd. antes → agora</th>
                                            <th className='py-1.5 pl-3 text-right font-semibold whitespace-nowrap'>Diferença no mês</th>
                                        </tr>
                                    </thead>
                                    <tbody className='divide-y divide-amber-200'>
                                        {dados.mudancas.map((m) => (
                                            <tr key={`${m.tipo}-${m.id}`}>
                                                <td className='py-1.5 pr-3'>
                                                    {m.descricao} <span className='text-xs text-amber-800'>({TIPO_MUDANCA[m.tipo] || m.tipo})</span>
                                                    {m.motivo && <div className='text-xs text-amber-900 mt-0.5'>{m.motivo}</div>}
                                                    <OrigemMudanca origem={m.origem} />
                                                </td>
                                                <td className='py-1.5 px-3 text-center whitespace-nowrap'>{m.quantidade_anterior} → {m.quantidade_atual}</td>
                                                <td className='py-1.5 pl-3 text-right font-semibold whitespace-nowrap'>{fmtDiferenca(m.diferenca)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    <div className='overflow-x-auto mb-4 rounded-lg border border-gray-200'>
                        <table className='w-full text-sm border-collapse min-w-[560px]'>
                            <thead>
                                <tr className='text-white text-left' style={{ backgroundColor: '#142a4c' }}>
                                    <th className='px-3 py-2.5 font-semibold'>Descrição</th>
                                    <th className='px-3 py-2.5 text-center font-semibold w-32 whitespace-nowrap'>Quantidade</th>
                                    <th className='px-3 py-2.5 text-right font-semibold w-32 whitespace-nowrap'>Valor Unitário</th>
                                    <th className='px-3 py-2.5 text-right font-semibold w-32'>Valor</th>
                                </tr>
                            </thead>
                            <tbody className='divide-y divide-gray-200'>
                                {(snap.itens || []).map((i) => (
                                    <tr key={i.id} className='odd:bg-white even:bg-gray-50'>
                                        <td className='px-3 py-2.5'>{i.descricao}</td>
                                        <td className='px-3 py-2.5 text-center'>{i.quantidade_implantada}</td>
                                        <td className='px-3 py-2.5 text-right whitespace-nowrap'>{fmtBRL(i.valor_unitario)}</td>
                                        <td className='px-3 py-2.5 text-right whitespace-nowrap'>{fmtBRL(i.valor_mes)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className='font-bold bg-gray-100 border-t-2 border-gray-300'>
                                    <td colSpan={3} className='px-3 py-3 text-right'>Total</td>
                                    <td className='px-3 py-3 text-right whitespace-nowrap'>{fmtBRL(dados.valor_total)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    {feedback && <div className='mb-4 p-3 rounded-lg bg-blue-50 text-blue-900'>{feedback}</div>}

                    {dados.respondivel && !modo && (
                        <div className='flex gap-4 justify-center my-6'>
                            <button onClick={() => { setModo('aprovar'); setFeedback(null); }}
                                className='px-8 py-3 rounded-lg text-white font-bold' style={{ backgroundColor: '#9ac31c' }}>
                                Aprovar
                            </button>
                            <button onClick={() => { setModo('contestar'); setFeedback(null); }}
                                className='px-8 py-3 rounded-lg text-white font-bold bg-red-500'>
                                Contestar
                            </button>
                        </div>
                    )}

                    {dados.respondivel && modo === 'aprovar' && (
                        <div className='border rounded-lg p-4 my-4'>
                            <h2 className='font-bold mb-2'>Confirmar aprovação</h2>
                            <input className='w-full border rounded-lg p-2 mb-3' placeholder='Seu nome'
                                value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
                            <div className='flex gap-3'>
                                <button onClick={aprovar} disabled={enviando}
                                    className='px-6 py-2 rounded-lg text-white font-bold disabled:opacity-50' style={{ backgroundColor: '#9ac31c' }}>
                                    {enviando ? 'Enviando...' : 'Confirmar aprovação'}
                                </button>
                                <button onClick={() => setModo(null)} className='px-6 py-2 rounded-lg bg-gray-200'>Voltar</button>
                            </div>
                        </div>
                    )}

                    {dados.respondivel && modo === 'contestar' && (
                        <div className='border rounded-lg p-4 my-4'>
                            <h2 className='font-bold mb-2'>Registrar contestação</h2>
                            <input className='w-full border rounded-lg p-2 mb-2' placeholder='Seu nome'
                                value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
                            <input className='w-full border rounded-lg p-2 mb-2' placeholder='Seu e-mail'
                                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                            <textarea className='w-full border rounded-lg p-2 mb-3' rows={4}
                                placeholder='Descreva o que está incorreto neste faturamento'
                                value={form.mensagem} onChange={(e) => setForm({ ...form, mensagem: e.target.value })} />
                            <div className='flex gap-3'>
                                <button onClick={contestar} disabled={enviando}
                                    className='px-6 py-2 rounded-lg text-white font-bold bg-red-500 disabled:opacity-50'>
                                    {enviando ? 'Enviando...' : 'Enviar contestação'}
                                </button>
                                <button onClick={() => setModo(null)} className='px-6 py-2 rounded-lg bg-gray-200'>Voltar</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FaturamentoPublico;
